<template>
  <div class="trust-badges">
    <div
      v-if="layout === 'horizontal'"
      class="flex flex-wrap items-center justify-center gap-6 sm:gap-8"
    >
      <div
        v-for="badge in badges"
        :key="badge.id"
        class="flex items-center space-x-2 text-gray-300"
      >
        <span v-if="badge.emoji" class="text-xl sm:text-2xl flex-shrink-0">{{ badge.emoji }}</span>
        <component v-else :is="getIconComponent(badge.icon)" class="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
        <span class="text-sm sm:text-base font-medium whitespace-nowrap">{{ badge.label }}</span>
      </div>
    </div>

    <div
      v-else-if="layout === 'vertical'"
      class="space-y-4"
    >
      <div
        v-for="badge in badges"
        :key="badge.id"
        class="flex items-start space-x-3 p-4 bg-gray-800 bg-opacity-50 rounded-lg"
      >
        <component :is="getIconComponent(badge.icon)" class="w-6 h-6 text-brand-primary flex-shrink-0 mt-0.5" />
        <div>
          <h4 class="text-white font-semibold mb-1">{{ badge.label }}</h4>
          <p v-if="badge.description" class="text-sm text-gray-400">{{ badge.description }}</p>
        </div>
      </div>
    </div>

    <div
      v-else-if="layout === 'grid'"
      class="grid grid-cols-2 md:grid-cols-3 gap-4"
    >
      <div
        v-for="badge in badges"
        :key="badge.id"
        class="flex flex-col items-center text-center p-4 bg-gray-800 bg-opacity-50 rounded-lg hover:bg-opacity-70 transition-all"
      >
        <component :is="getIconComponent(badge.icon)" class="w-8 h-8 text-brand-primary mb-2" />
        <span class="text-sm font-medium text-white">{{ badge.label }}</span>
        <p v-if="badge.description" class="text-xs text-gray-400 mt-1">{{ badge.description }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { h } from 'vue';

defineProps({
  badges: {
    type: Array,
    required: true,
    validator: (badges) => {
      return badges.every(badge =>
        badge.id && (badge.icon || badge.emoji) && badge.label
      );
    }
  },
  layout: {
    type: String,
    default: 'horizontal',
    validator: (value) => ['horizontal', 'vertical', 'grid'].includes(value)
  }
});

// Icon mapping helper
const getIconComponent = (iconName) => {
  const icons = {
    shield: (props) => h('svg', {
      ...props,
      fill: 'currentColor',
      viewBox: '0 0 20 20',
      xmlns: 'http://www.w3.org/2000/svg'
    }, [
      h('path', {
        fillRule: 'evenodd',
        d: 'M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
        clipRule: 'evenodd'
      })
    ]),
    lock: (props) => h('svg', {
      ...props,
      fill: 'currentColor',
      viewBox: '0 0 20 20',
      xmlns: 'http://www.w3.org/2000/svg'
    }, [
      h('path', {
        fillRule: 'evenodd',
        d: 'M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z',
        clipRule: 'evenodd'
      })
    ]),
    check: (props) => h('svg', {
      ...props,
      fill: 'currentColor',
      viewBox: '0 0 20 20',
      xmlns: 'http://www.w3.org/2000/svg'
    }, [
      h('path', {
        fillRule: 'evenodd',
        d: 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
        clipRule: 'evenodd'
      })
    ]),
    free: (props) => h('svg', {
      ...props,
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      xmlns: 'http://www.w3.org/2000/svg'
    }, [
      h('path', {
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeWidth: '2',
        d: 'M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7'
      })
    ]),
    star: (props) => h('svg', {
      ...props,
      fill: 'currentColor',
      viewBox: '0 0 20 20',
      xmlns: 'http://www.w3.org/2000/svg'
    }, [
      h('path', {
        d: 'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z'
      })
    ]),
    lightning: (props) => h('svg', {
      ...props,
      fill: 'currentColor',
      viewBox: '0 0 20 20',
      xmlns: 'http://www.w3.org/2000/svg'
    }, [
      h('path', {
        fillRule: 'evenodd',
        d: 'M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z',
        clipRule: 'evenodd'
      })
    ]),
    maple: (props) => h('svg', {
      ...props,
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      xmlns: 'http://www.w3.org/2000/svg'
    }, [
      h('path', {
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeWidth: '2',
        d: 'M3 21h18M5 21V7l7-4 7 4v14M9 9h6M9 13h6M9 17h6'
      })
    ]),
    gift: (props) => h('svg', {
      ...props,
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      xmlns: 'http://www.w3.org/2000/svg'
    }, [
      h('path', {
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeWidth: '2',
        d: 'M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7'
      })
    ]),
    clock: (props) => h('svg', {
      ...props,
      fill: 'currentColor',
      viewBox: '0 0 20 20',
      xmlns: 'http://www.w3.org/2000/svg'
    }, [
      h('path', {
        fillRule: 'evenodd',
        d: 'M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z',
        clipRule: 'evenodd'
      })
    ]),
    globe: (props) => h('svg', {
      ...props,
      fill: 'currentColor',
      viewBox: '0 0 20 20',
      xmlns: 'http://www.w3.org/2000/svg'
    }, [
      h('path', {
        fillRule: 'evenodd',
        d: 'M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z',
        clipRule: 'evenodd'
      })
    ])
  };

  return icons[iconName] || icons.gift || icons.check;
};
</script>
